import { api } from 'API'
import { PlayerProfile, PlayerProfileUpdate, PlayerMetric, CreateMetricPayload, DashboardData } from 'types/player'

// ─── Player Profile ─────────────────────────────────────────────

/** GET /users/:userId/player-profile */
export async function getPlayerProfile(userId: number): Promise<PlayerProfile> {
  const response = await api.get(`/users/${userId}/player-profile`)
  return response.data as PlayerProfile
}

/** PATCH /users/:userId/player-profile — self-only */
export async function updatePlayerProfile(userId: number, data: PlayerProfileUpdate): Promise<PlayerProfile> {
  const response = await api.patch(`/users/${userId}/player-profile`, data)
  return response.data as PlayerProfile
}

// ─── Player Metrics ─────────────────────────────────────────────

/** GET /users/:userId/metrics */
export async function getPlayerMetrics(userId: number): Promise<PlayerMetric[]> {
  const response = await api.get(`/users/${userId}/metrics`)
  return response.data as PlayerMetric[]
}

/** POST /users/:userId/metrics — self-entry only */
export async function addPlayerMetric(userId: number, data: CreateMetricPayload): Promise<PlayerMetric> {
  const response = await api.post(`/users/${userId}/metrics`, data)
  return response.data as PlayerMetric
}

/** PATCH /metrics/:metricId/certify — org-scoped coach certification */
export async function certifyMetric(metricId: number): Promise<PlayerMetric> {
  const response = await api.patch(`/metrics/${metricId}/certify`)
  return response.data as PlayerMetric
}

// ─── Dashboard ──────────────────────────────────────────────────

/** GET /users/:userId/dashboard — cross-org personal dashboard */
export async function getDashboard(userId: number, limit = 20): Promise<DashboardData> {
  const response = await api.get(`/users/${userId}/dashboard`, {
    params: { limit }
  })
  return response.data as DashboardData
}

/** One schedule row for the player dashboard (dates are ISO strings over JSON). */
export interface PlayerScheduleEvent {
  id: number
  name: string
  scheduledDate: string
  startTime: string
  endTime: string
  duration: number
  location: string
  organizationId: number
  orgName: string
}

/**
 * GET /player/schedule — the active athlete profile's practice events in
 * [from, to] across every team, plus a count of events still ahead of now.
 * Replaces the old FE fan-out (getMyTeams + per-org searchEvents + merge).
 *
 * `scope` narrows both to one organization and/or one Team the player is on;
 * omitted means all of them.
 */
export async function getPlayerSchedule(
  from: string,
  to: string,
  scope: { organizationId?: number; teamId?: number } = {}
): Promise<{ events: PlayerScheduleEvent[]; upcomingCount: number }> {
  const response = await api.get('/player/schedule', {
    params: { from, to, organizationId: scope.organizationId, teamId: scope.teamId }
  })
  return response.data as { events: PlayerScheduleEvent[]; upcomingCount: number }
}

/** Drill-completion driven stats for the play dashboard (streak + weekly completion). */
export interface PlayerDashboardStats {
  /** Consecutive days up to today with >=1 drill completed. */
  streakDays: number
  /** Mon..Sun of the current week; active = >=1 drill completed that day. */
  weekDots: { label: string; active: boolean }[]
  /** Overall = total completed drills / total assigned drills across active assignments, 0-100. */
  completionPercent: number
  /** Drills completed this calendar week (for the ↑/↓ trend). */
  weeklyCompletedCount: number
  /** Drills completed last calendar week (for the ↑/↓ trend). */
  previousWeekCompletedCount: number
}

/**
 * GET /player/dashboard-stats — streak + weekly drill completion for the active
 * athlete profile (scoped via X-Active-Profile-Id, like the rest of /player/*).
 *
 * Passes the browser IANA timezone so streak/weekDots day boundaries align to
 * the player's local calendar (the data model has no stored user timezone).
 */
export async function getDashboardStats(): Promise<PlayerDashboardStats> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const response = await api.get('/player/dashboard-stats', {
    params: tz ? { tz } : undefined
  })
  return response.data as PlayerDashboardStats
}
